import type { OutgoingIntakeFile } from "../../api/types/outgoingIntake";

/**
 * Extraction states, spelled exactly as the API sends them.
 *
 * `ready` rather than `succeeded`: these mirror the server's own vocabulary, and
 * a value it never emits leaves the UI waiting for a state that cannot arrive.
 */
export const EXTRACTION_PENDING = "pending";
export const EXTRACTION_RUNNING = "running";
export const EXTRACTION_READY = "ready";
export const EXTRACTION_FAILED = "failed";

export type ExtractionStatus =
  | typeof EXTRACTION_PENDING
  | typeof EXTRACTION_RUNNING
  | typeof EXTRACTION_READY
  | typeof EXTRACTION_FAILED;

/** Whether extraction has stopped moving, either way. */
export function isExtractionSettled(status: string): boolean {
  return status === EXTRACTION_READY || status === EXTRACTION_FAILED;
}

/** An attachment the client gave Casey, plus how its extraction is going. */
export type CaseyAttachment = {
  attachment_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  // Widened to `string` because the server owns this value: an unrecognized one
  // should render as still-working, not fail to typecheck.
  extraction_status: ExtractionStatus | string;
  document_type?: string | null;
  candidate_count?: number;
  /**
   * Whether the copy the client can be shown exists yet.
   *
   * The server writes it just after the extraction result, so this turns true a
   * moment later than `extraction_status` does -- and never, for a file Extend
   * could not render. Only then can the attachment be opened in the viewer.
   */
  has_citation_pdf?: boolean;
};

/**
 * A single suggested answer, streamed as part of a `data-proposals` part.
 *
 * Nothing here is written to the form until the client accepts it, so every
 * field exists to help them decide: `reasoning` says why Casey thinks so, and
 * the document trio says where it read it.
 */
export type CaseyProposal = {
  proposal_id: string;
  /** `document` offers an uploaded file for a document request; `answer` a value. */
  kind?: "answer" | "document";
  question_id: string;
  /** The chat attachment a document proposal offers, before it is a real file. */
  attachment_id?: string | null;
  repeatable_group_id?: string | null;
  repeatable_group_instance_id?: string | null;
  repeatable_group_title?: string | null;
  /** True when accepting starts a new entry rather than filling one that exists. */
  creates_group_entry?: boolean;
  prompt: string;
  question_type?: string | null;
  value: unknown;
  display_value: string;
  reasoning?: string | null;
  origin: "document" | "conversation" | string;
  document_filename?: string | null;
  reference_text?: string | null;
  page?: number | null;
  confidence?: number | null;
  /**
   * Where the value sits on that page, for highlighting it in the document.
   *
   * In points, in the coordinate space of `page_width` by `page_height`, so all
   * three plus `has_citation_pdf` are needed to draw anything. When any is
   * missing the card quotes `reference_text` instead.
   */
  polygon?: { x: number; y: number }[] | null;
  page_width?: number | null;
  page_height?: number | null;
  has_citation_pdf?: boolean;
  /** Set when the proposal links an uploaded file to a document request. */
  file?: OutgoingIntakeFile | null;
};

export type CaseyProposalsData = {
  proposals: CaseyProposal[];
};

/** A short note about what Casey is doing, shown while a turn is in flight. */
export type CaseyProgressData = {
  label: string;
  tool: string;
};

export type CaseyReferralData = {
  kind: "attorney_referral";
  message: string;
  /** Who answers instead, safe to render whatever name is missing. */
  who?: string | null;
  attorney_name: string | null;
  law_firm_name: string | null;
  topic?: string | null;
};

export type CaseyLanguageData = {
  language: string;
};

/**
 * Custom data parts Casey's stream can carry, keyed by the part name the
 * backend emits. `useChat` surfaces these as `data-<key>` parts.
 */
export type CaseyDataParts = {
  proposals: CaseyProposalsData;
  "casey-progress": CaseyProgressData;
  referral: CaseyReferralData;
  language: CaseyLanguageData;
};

/**
 * The decision a client has made about a proposal, tracked per proposal id.
 *
 * `applying` is the gap where accepting a document has to reach the server to
 * turn the chat attachment into a file on the intake. Everything else settles
 * locally and never passes through it.
 */
export type ProposalDecision = "pending" | "applying" | "accepted" | "rejected";

/**
 * What the intake's bottom-right control cluster is showing.
 *
 * The Casey launcher parks immediately to the left of that cluster, so it has to
 * know how much room to leave. This is passed down from the page rather than
 * measured at runtime because the cluster mounts and unmounts with the step, and
 * an observer would have nothing to watch in the states where it is absent.
 */
export type IntakeControlsLayout =
  | "hidden"
  | "settings-only"
  | "with-step-arrows";

/**
 * The decisions worth remembering between visits.
 *
 * `pending` is the absence of a decision and `applying` describes a request in
 * flight, so neither is stored: a card left undecided should come back offering
 * the choice again.
 */
export type SettledProposalDecision = "accepted" | "rejected";

/**
 * The conversation as it stands on the server, replayed when the panel mounts.
 *
 * Messages are `UIMessage`-shaped and handed straight to `useChat`, which is why
 * the parts are not narrowed here: they are the same parts the stream emitted.
 */
export type CaseyConversation = {
  messages: {
    id: string;
    role: "user" | "assistant";
    parts: unknown[];
  }[];
  decisions: Record<string, SettledProposalDecision>;
  language: string | null;
  attachments: CaseyAttachment[];
};
